// @ts-nocheck
/**
 * Gateway (server) list — redesigned to obd3-gui pattern (FillPage +
 * PageHeader + DataTable + tinted row-action buttons + ConfirmDialog).
 * Preserved: hasPermission read guard, multi/single delete, name/ip/os/
 * type search (delegated to DataTable's built-in search), CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_SERVER_MANAGEMENT_LIST,
  API_SERVER_MANAGEMENT_DELETE,
} from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ServerManagementList = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [servers, setServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [moduleOperations, setModuleOperations] = useState<any>({});
  const [selectedServers, setSelectedServers] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(
        parseModuleOperations(user.acl_settings.module_operations),
      );
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchList();
    const user = getUser();
    if (user) {
      if (user.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
      if (user?.acl_settings) {
        setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperations(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_SERVER_MANAGEMENT_LIST)
      .then((resp: any) => {
        const list = (resp || []).map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
        }));
        setServers(list);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCheckboxToggle = (id: any) => {
    setSelectedServers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedServers.length === servers.length) setSelectedServers([]);
    else setSelectedServers(servers.map((s: any) => s.id));
  };

  const requestSingleDelete = (row: any) => {
    checkAuth(setToastProps, setShowToast);
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    const ids = pendingBulk
      ? selectedServers
      : pendingDelete
      ? [pendingDelete.id]
      : [];
    if (!ids.length) return;
    setDeleting(true);
    try {
      await postApiCall(API_SERVER_MANAGEMENT_DELETE, { serverIds: ids });
      toast.success('Gateway deleted successfully');
      if (pendingBulk) setSelectedServers([]);
      setPendingDelete(null);
      setPendingBulk(false);
      setLoading(true);
      fetchList();
    } catch (err: any) {
      if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err));
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'gateway-management-list',
        data: servers,
        columns: [
          {
            header: 'Data Center',
            accessor: (row: any) => row.data_center?.name || '-',
          },
          { header: 'Type', accessor: (row: any) => row.type || '-' },
          { header: 'Gateway IP', accessor: (row: any) => row.server_ip || '-' },
          {
            header: 'Gateway Name',
            accessor: (row: any) => row.server_name || '-',
          },
          { header: 'OS', accessor: (row: any) => row.os || '-' },
          { header: 'CPU Cores', accessor: (row: any) => row.cpu_cores || '-' },
          { header: 'RAM', accessor: (row: any) => row.ram || '-' },
          { header: 'Hard Disk', accessor: (row: any) => row.hard_disk || '-' },
          {
            header: 'Created Date',
            accessor: (row: any) => formatDate(row.createdAt),
          },
          {
            header: 'Updated Date',
            accessor: (row: any) => formatDate(row.updatedAt),
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.GATEWAY_MANAGEMENT,
    'read',
  );
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const canAdd = isSuperAdmin;
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={
              selectedServers.length === servers.length && servers.length > 0
            }
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedServers.includes(row.original.id)}
            onCheckedChange={() => handleCheckboxToggle(row.original.id)}
            aria-label="Select row"
          />
        ),
      },
      {
        accessorFn: (row: any) => row.data_center?.name,
        id: 'data_center.name',
        header: 'Data Center',
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 120,
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
            {row.original.type}
          </span>
        ),
      },
      {
        accessorKey: 'server_ip',
        header: 'Gateway IP',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.server_ip || '—'}</span>
        ),
      },
      {
        accessorKey: 'server_name',
        header: 'Gateway Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.SERVER_MANAGEMENT.EDIT.replace(
                ':id',
                row.original.id,
              )}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.server_name}
            </Link>
          ) : (
            <span className="font-medium text-foreground">
              {row.original.server_name}
            </span>
          ),
      },
      { accessorKey: 'os', header: 'OS' },
      { accessorKey: 'cpu_cores', header: 'CPU Cores' },
      { accessorKey: 'ram', header: 'RAM' },
      { accessorKey: 'hard_disk', header: 'Hard Disk' },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.updatedAt)}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/${ROUTES.SERVER_MANAGEMENT.EDIT.replace(
                      ':id',
                      row.original.id,
                    )}`,
                  );
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {selectedServers.length === 0 && canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  requestSingleDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }
    return cols;
  }, [
    selectedServers,
    servers,
    canEdit,
    canDelete,
    showActions,
    navigate,
  ]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Gateway Management" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Gateway Management"
        description="SIP gateway records — media servers, PBX endpoints, and routing back-ends."
        actions={
          <>
            {canDelete && selectedServers.length > 0 && (
              <Button variant="destructive" onClick={() => setPendingBulk(true)}>
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedServers.length})
              </Button>
            )}
            {servers.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.SERVER_MANAGEMENT.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Gateway
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="server-management.list:v1"
        columns={columns}
        data={servers}
        isLoading={loading}
        searchPlaceholder="Search by name, IP, OS, type…"
        emptyTitle="No gateways"
        emptyDescription="No gateways match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected gateways?' : 'Delete this gateway?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">
                {selectedServers.length}
              </span>{' '}
              gateway(s) will be permanently deleted.
            </>
          ) : (
            <>
              Gateway{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.server_name}
              </span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default ServerManagementList;
